import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Palette, ArrowRight } from "lucide-react";
import { Reveal, SectionTitle } from "./Shared";
import { shadeGroups } from "../mock";

const isGradient = (v) => v.startsWith("linear-gradient");

const Swatch = ({ shade, productId, groupId }) => {
  const [copied, setCopied] = useState(false);
  const quoteLink = {
    pathname: "/contact",
    search: `?product=${productId}&shade=${encodeURIComponent(`${shade.code} ${shade.name}`)}`,
    hash: "#quote",
  };
  return (
    <Link
      to={quoteLink}
      className="swatch group"
      title={`Request a quote for ${shade.name}`}
      data-testid={`swatch-${shade.code.toLowerCase()}`}
      onMouseEnter={() => setCopied(false)}
    >
      <span
        className={`swatch__chip ${shade.light ? "swatch__chip--light" : ""}`}
        style={isGradient(shade.hex) ? { backgroundImage: shade.hex } : { backgroundColor: shade.hex }}
      >
        <span className="swatch__cta">
          {copied ? <Check size={14} /> : <ArrowRight size={14} />} Quote
        </span>
      </span>
      <span className="swatch__meta">
        <span className="swatch__code">{shade.code}</span>
        <span className="swatch__name">{shade.name}</span>
      </span>
    </Link>
  );
};

const ShadeCatalogue = ({ compact = false, groups = shadeGroups }) => {
  const [active, setActive] = useState(groups[0].id);
  const group = groups.find((g) => g.id === active) || groups[0];

  return (
    <section className={`section ${compact ? "bg-white" : "bg-[#f7f7f7]"}`} id="shades" data-testid="shade-catalogue">
      <div className="container-dic">
        <Reveal>
          <SectionTitle sub="Pick a shade to open a quote request with the product and shade already filled in. Screen colours are approximations - physical shade cards available on request.">
            Standard Shade Card
          </SectionTitle>
        </Reveal>

        <Reveal className="flex flex-wrap justify-center gap-2 mb-8" role="tablist">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={active === g.id}
              onClick={() => setActive(g.id)}
              className={`filter-pill ${active === g.id ? "is-active" : ""}`}
              data-testid={`shade-group-${g.id}`}
            >
              {g.title}
            </button>
          ))}
        </Reveal>

        <Reveal key={group.id} className="mb-6 text-center text-[14px] text-[#58595b] flex items-center justify-center gap-2">
          <Palette size={16} className="text-[#7ab929]" /> {group.note}
        </Reveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="swatch-grid">
          {group.shades.map((s, i) => (
            <Reveal key={s.code} delay={(i % 6) * 50}>
              <Swatch shade={s} productId={group.product} groupId={group.id} />
            </Reveal>
          ))}
        </div>

        <div className="text-center mt-10 text-[14px] text-[#58595b]">
          Need a shade that is not listed?{" "}
          <Link to={{ pathname: "/contact", search: "?product=custom-colour", hash: "#quote" }} className="text-[#7ab929] font-bold hover:underline" data-testid="shade-custom-link">
            Request a custom colour match &rsaquo;
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ShadeCatalogue;
